import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { resolveMediaUrl } from '../utils/mediaUrl';

const AuthContext = createContext();

const API = '/api/v1';
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const CACHED_USER_KEY = 'cached_user';

// ✅ Server ke liye empty baseURL
export const authAxios = axios.create({
  baseURL: '',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) return Promise.reject(error);
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          original.headers.Authorization = `Bearer ${newToken}`;
          return authAxios(original);
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
        const newAccessToken = res.data.data?.access_token;
        if (!newAccessToken) throw new Error('No access token in refresh response');
        localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
        authAxios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        original.headers.Authorization = `Bearer ${newAccessToken}`;
        return authAxios(original);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(CACHED_USER_KEY);
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const mergeUserData = (data) => {
  const profile = data.profile ? { ...data.profile, profile_photo: resolveMediaUrl(data.profile.profile_photo) } : null;
  const documents = (data.documents || []).map((doc) => ({ ...doc, file_url: resolveMediaUrl(doc.file_url) }));
  return {
    ...data.user,
    profile,
    kopartner_profile: data.kopartner_profile || null,
    documents,
    payment: data.payment || { has_payment: false, requires_payment: false }
  };
};

const isEqual = (obj1, obj2) => {
  if (!obj1 || !obj2) return false;
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  const fetchCurrentUser = useCallback(async () => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!accessToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAxios.get(`${API}/user/me`);
      const merged = mergeUserData(response.data.data);
      
      const currentPath = window.location.pathname;
      const isPaymentPage = currentPath === '/membership' || 
                            currentPath === '/services' || 
                            currentPath.startsWith('/payment');
      
      if (merged.payment?.requires_payment && !isPaymentPage) {
        console.log('⚠️ Payment required, clearing user data');
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(CACHED_USER_KEY);
        setUser(null);
        setLoading(false);
        return;
      }
      
      if (merged.payment?.requires_payment && isPaymentPage) {
        console.log('ℹ️ On payment page, allowing access');
        setUser(prevUser => isEqual(prevUser, merged) ? prevUser : merged);
        localStorage.setItem(CACHED_USER_KEY, JSON.stringify(merged));
        setLoading(false);
        return;
      }
      
      setUser(prevUser => isEqual(prevUser, merged) ? prevUser : merged);
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(merged));
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(CACHED_USER_KEY);
        setUser(null);
      } else {
        const cached = localStorage.getItem(CACHED_USER_KEY);
        if (cached) {
          try {
            const cachedUser = JSON.parse(cached);
            const currentPath = window.location.pathname;
            const isPaymentPage = currentPath === '/membership' || 
                                  currentPath === '/services' || 
                                  currentPath.startsWith('/payment');
            
            if (cachedUser.payment?.requires_payment && !isPaymentPage) {
              localStorage.removeItem(ACCESS_TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              localStorage.removeItem(CACHED_USER_KEY);
              setUser(null);
              setLoading(false);
              return;
            }
            setUser(prev => isEqual(prev, cachedUser) ? prev : cachedUser);
          } catch (_) {}
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    const handler = () => {
      setUser(null);
      setLoading(false);
    };
    logoutRef.current = handler;
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // ✅ Fixed login - always store tokens
  const login = (accessToken, refreshToken, userData) => {
    // ✅ Always store tokens in localStorage
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
    
    // ✅ Check if payment is required
    if (userData?.payment?.requires_payment) {
      console.log('⚠️ Payment required, login blocked');
      setUser(userData);
      return;
    }
    
    setUser(userData);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await authAxios.post(`${API}/auth/logout`, { refresh_token: refreshToken });
      } catch (err) {}
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(CACHED_USER_KEY);
    setUser(null);
  };

  const refreshUser = useCallback(async (options = {}) => {
    const { force = false } = options;
    try {
      const response = await authAxios.get(`${API}/user/me`);
      const merged = mergeUserData(response.data.data);
      
      const currentPath = window.location.pathname;
      const isPaymentPage = currentPath === '/membership' || 
                            currentPath === '/services' || 
                            currentPath.startsWith('/payment');
      
      if (merged.payment?.requires_payment && !isPaymentPage) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(CACHED_USER_KEY);
        setUser(null);
        return null;
      }
      
      setUser(prevUser => {
        if (!force && isEqual(prevUser, merged)) return prevUser;
        localStorage.setItem(CACHED_USER_KEY, JSON.stringify(merged));
        return merged;
      });
      return merged;
    } catch (error) {
      return user;
    }
  }, [user]);

  const updateUser = (updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const updateProfilePhoto = useCallback((photoUrl) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        profile: {
          ...prev.profile,
          profile_photo: resolveMediaUrl(photoUrl),
        },
      };
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

  const contextValue = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    updateUser,
    updateProfilePhoto,
    getAccessToken,
    token: localStorage.getItem(ACCESS_TOKEN_KEY),
    isAuthenticated: !!user && !user?.payment?.requires_payment,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
